import { LiaDataElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { ago, number } from '../../shared/ui/format.js';
import { errorPanel, loadingPanel } from '../../shared/ui/templates.js';
import { breadcrumb } from '../../shared/ui/breadcrumb.js';
import { overview } from '../api/developer.js';
import { accountIcon, accountPath, stateVariant, word } from '../accounts.js';
import '../ui/create-team.js';

/**
 * Every team this person is part of, as a table.
 *
 * One row per team, with what a developer's relationship to it is: they <b>own</b> it, in which case
 * its pages are theirs to open, or they <b>hold a seat</b> in it, in which case what they can see is
 * the team's overview and their own seat on it. An owner who has never run anything is here too, with
 * no seat: the team is still theirs to run. Their personal team is one row among the others, with no
 * plan and no seats, because it has neither.
 *
 * Creating a team is this page's one action: a name, and the person who typed it owns it. Somebody
 * else's team is reached by invitation, which is theirs to send.
 *
 * The list comes from the same request as the profile, `GET /api/v1/seats/me/overview`, which is only
 * ever about the caller. Which teams exist is not something a developer is told.
 */
export class UserAccounts extends LiaDataElement
{
   static properties = {
      data: { state: true },
      creating: { state: true }
   };

   constructor()
   {
      super();
      this.data = null;
      this.creating = false;
   }

   async load(signal)
   {
      this.data = await overview(signal);
   }

   render()
   {
      const accounts = this.data?.accounts ?? [];

      return html`
         <lia-page
            title="Teams"
            icon="fa-solid fa-people-group"
            description="The teams you own and the ones you hold a seat in."
            .actions=${[{ id: 'create', label: 'Create a team', icon: 'fa-solid fa-plus', variant: 'primary', disabled: this.creating }]}
            @lia-action=${event => { if (event.detail?.action?.id === 'create') { this.creating = true; } }}
         >
            <div class="d-flex flex-column gap-3">
               ${breadcrumb([{ label: 'Teams' }])}
               ${this.error ? errorPanel(this.error, () => this.reload()) : nothing}

               ${this.creating
                  ? html`<user-create-team cancellable @user-create-team-cancel=${() => { this.creating = false; }}></user-create-team>`
                  : nothing}

               <p class="text-body-secondary small mb-0">
                  Public repositories are free everywhere. Private ones are one at a time under personal
                  use; a seat in a team covers them all.
               </p>

               ${!this.data && !this.error
                  ? loadingPanel('Loading your teams')
                  : accounts.length === 0
                     ? this.empty
                     : this.table(accounts)}
            </div>
         </lia-page>`;
   }

   get empty()
   {
      return html`
         <lia-empty-state
            centered
            icon="fa-solid fa-people-group"
            title="No teams yet"
            message="Create a team, or ask the owner of one for an invitation."
            .actions=${[{ id: 'create', label: 'Create a team', icon: 'fa-solid fa-plus', variant: 'primary' }]}
            @lia-action=${() => { this.creating = true; }}
         ></lia-empty-state>`;
   }

   table(accounts)
   {
      return html`
         <lia-card>
            <lia-data-table
               listing-id="accounts"
               .perPage=${10}
               no-column-manager
               .columns=${[
                  {
                     key: 'name',
                     label: 'Team',
                     sortable: true,
                     searchable: true,
                     render: row => html`
                        <a class="fw-semibold text-decoration-none" href=${accountPath('overview', row.id)}>
                           <i class="${accountIcon(row)} me-2" aria-hidden="true"></i>${row.name ?? row.id}
                        </a>
                        ${row.personal
                           ? html`<div class="small text-body-secondary">Your personal team</div>`
                           : row.slug && row.slug !== row.name
                              ? html`<div class="small text-body-secondary">${row.slug}</div>`
                              : nothing}`
                  },
                  {
                     key: 'owner',
                     label: 'Your role',
                     sortable: true,
                     render: row => (row.personal
                        ? html`<lia-badge variant="secondary">Personal</lia-badge>`
                        : row.owner
                           ? html`<lia-badge variant="primary">Owner</lia-badge>`
                           : html`<lia-badge variant="secondary">Member</lia-badge>`)
                  },
                  { key: 'plan', label: 'Plan', sortable: true, render: row => (row.personal ? '—' : word(row.plan)) },
                  {
                     key: 'countingSeats',
                     label: 'Seats in use',
                     sortable: true,
                     render: row => (row.personal
                        ? '—'
                        : html`
                           <span class=${row.overQuota ? 'text-warning' : ''}>
                              ${number(row.countingSeats ?? 0)} of ${number((row.seatQuota ?? 0) + (row.includedFreeSeats ?? 0))}
                              ${row.overQuota ? html`<span class="ms-1 small">over quota</span>` : nothing}
                           </span>`)
                  },
                  {
                     key: 'seat',
                     label: 'Your seat',
                     sortable: false,
                     render: row => (row.personal
                        ? '—'
                        : row.seat
                           ? html`<lia-badge variant=${stateVariant(row.seat.state)}>${word(row.seat.state)}</lia-badge>`
                           : html`<span class="text-body-secondary">None</span>`)
                  },
                  {
                     key: 'lastRun',
                     label: 'Your last run',
                     sortable: false,
                     render: row => (row.seat?.lastSeenAt ? ago(row.seat.lastSeenAt) : 'Never')
                  }
               ]}
               .rows=${accounts}
               .rowKey=${row => row.id}
               .rowActions=${row => [
                  { id: 'open', icon: 'fa-solid fa-arrow-right', title: 'Open', variant: 'primary', size: 'sm', href: accountPath('overview', row.id) },
                  ...(row.owner && !row.personal
                     ? [
                        { id: 'members', icon: 'fa-solid fa-users', title: 'Members', variant: 'outline-secondary', size: 'sm', href: accountPath('members', row.id) },
                        { id: 'billing', icon: 'fa-solid fa-credit-card', title: 'Billing', variant: 'outline-secondary', size: 'sm', href: accountPath('billing', row.id) }
                     ]
                     : [])
               ]}
            ></lia-data-table>
         </lia-card>`;
   }
}

customElements.define('user-accounts', UserAccounts);
